import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LikeOutlined,
  SendOutlined,
  ShareAltOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Application from '../application/application';
import JobFitCard from './JobFitCard';
import { JobContent } from '../../models';
import { JobMetrics } from '../../models/jobMetrics.model';
import { JobService } from '../../services/job.service';
import ReactGA from 'react-ga4';
import Swal from 'sweetalert2';
import './detail.css';
import './quill-rich-content.css';
import JobDescription from './JobDescription';
import { containsHtml } from '../../utils/formatPlainJobDescription';
import { normalizeFeaturedRichHtml } from '../../utils/normalizeFeaturedRichHtml';

const Detail: React.FC = () => {
  const [metrics, setMetrics] = useState<JobMetrics>(new JobMetrics());
  const [job, setJob] = useState<JobContent>(new JobContent());
  const [spinner, setSpinner] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [likeDisabled, setLikeDisabled] = useState<boolean>(false);

  const { job_Id } = useParams<{ job_Id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      try {
        if (!job_Id) {
          navigate('/jobs');
          return;
        }

        const currentJob = await JobService.GetJob(job_Id);
        const currentMetrics = await JobService.GetMetrics(job_Id);
        const views = await JobService.AddView(job_Id);
        const shouldDisableLike = await JobService.ValidateDisable(job_Id);

        ReactGA.send({
          hitType: 'pageview',
          page: '/jobs/' + job_Id,
          title: currentJob.company + ' : ' + currentJob.name,
        });

        setJob(currentJob);
        setMetrics({ ...currentMetrics, VIEWS: views });
        setLikeDisabled(shouldDisableLike);
      } catch (error: any) {
        if (error.message.includes('SHOW: ')) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: error.message.slice(5, error.message.length),
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer);
              toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
          });
        }

        navigate('/jobs');
      } finally {
        setSpinner(false);
      }
    };

    initialize();
  }, [job_Id, navigate]);

  const trackEvent = (category = '', label = '') => {
    try {
      ReactGA.event({
        category,
        action: 'Clicked on ' + label,
        label,
      });
    } catch {
      // Analytics must never block a job action or navigation.
    }
  };

  const handleApply = () => {
    if (!job.published || job.closed) return;

    const label = job.category + ' : ' + job.subCategory + ' : ' + job.name;
    trackEvent('Apply Job', label);

    if (job.featured) setIsOpen(true);
  };

  const renderApplyAction = (className: string) => {
    const content = (
      <>
        <SendOutlined />
        {job.featured ? 'Easy Apply' : 'Apply now'}
      </>
    );

    if (!job.featured && job.published && !job.closed && job.shortURL.longUrl) {
      return (
        <a
          href={job.shortURL.longUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={handleApply}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        type="button"
        className={className}
        onClick={handleApply}
        disabled={!job.published || job.closed || !job.featured}
      >
        {content}
      </button>
    );
  };

  const handleLike = async () => {
    if (likeDisabled || !job.id) return;

    const label = job.category + ' : ' + job.subCategory + ' : ' + job.name;
    trackEvent('Like', label);

    const likes = await JobService.AddLike(job.id);
    setMetrics((current) => ({ ...current, LIKES: likes }));
    setLikeDisabled(true);
  };

  const shareJob = () => {
    const currentUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: job.name,
        text: job.company,
        url: currentUrl,
      });
      return;
    }

    navigator.clipboard?.writeText(currentUrl);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Job link copied.',
      showConfirmButton: false,
      timer: 2200,
    });
  };

  const locationLabel =
    [job.location, job?.countries?.join(' / ')].filter(Boolean).join(' - ') ||
    'Location flexible';

  const categoryTags = [job.category, job.subCategory].filter(Boolean);

  const aboutUs = job.aboutUs?.trim() ?? '';

  return (
    <main className="public-page-shell w-full">
      {spinner && (
        <div className="spinner-container">
          <span className="spinner"></span>
        </div>
      )}

      <div className="public-content-rail ats-stack">
        <Link
          to="/jobs"
          className="public-button-secondary w-fit px-4 md:hidden"
        >
          <ArrowLeftOutlined />
          Back to Jobs
        </Link>

        <section className="public-panel">
          <div className="public-panel-header">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="public-eyebrow flex items-center gap-2">
                  <ApartmentOutlined />
                  {job.company || 'Company'}
                </div>
                <h1 className="jo-heading m-0 break-words text-2xl font-black leading-tight sm:text-3xl lg:text-4xl">
                  {job.name}
                </h1>
              </div>

              {renderApplyAction(
                'public-button-primary job-detail-apply job-detail-apply-header w-full sm:w-auto'
              )}
            </div>
          </div>

          <div className="public-panel-body ats-stack">
            <div className="ats-meta-grid ats-meta-grid--detail">
              <span className="ats-meta-item">
                <EnvironmentOutlined />
                <span className="truncate">{locationLabel}</span>
              </span>
              <span className="ats-meta-item ats-meta-item--end">
                <CalendarOutlined />
                <span className="truncate">{job.postedDate}</span>
              </span>
            </div>

            {categoryTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {categoryTags.map((tag) => (
                  <span key={tag} className="ats-category-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {job.remote && (
                <span className="job-detail-remote-pill">Remote</span>
              )}
              {job.closed && <span className="jo-status-closed">Closed</span>}
              {job.skills?.map((skill) => (
                <span
                  key={skill.id || skill.skillName}
                  className="ats-skill-chip"
                >
                  {skill.skillName}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[1fr_260px]">
          <div className="public-panel min-w-0">
            <div className="public-panel-body">
              <h2 className="jo-heading mb-4 text-xl font-black">
                Job Description
              </h2>
              <JobDescription
                description={job.description}
                renderAsHtml={job.featured}
              />
              <div className="job-detail-actions">
                <Link
                  to="/jobs"
                  className="public-button-secondary job-detail-back"
                >
                  <ArrowLeftOutlined />
                  Back to Jobs
                </Link>
                {renderApplyAction('public-button-primary job-detail-apply')}
              </div>
            </div>
          </div>

          <aside className="job-detail-sidebar ats-stack min-w-0">
            {!job.remote && (
              <JobFitCard
                jobId={job.id}
                jobTitle={job.name}
                company={job.company}
                featured={job.featured}
                onEasyApply={handleApply}
              />
            )}
            <div className="public-panel">
              <div className="public-panel-body ats-stack">
                <button
                  className="public-button-secondary px-4"
                  onClick={handleLike}
                  disabled={likeDisabled}
                >
                  <LikeOutlined />
                  {metrics.LIKES}
                </button>
                <div className="public-button-secondary px-4">
                  <EyeOutlined />
                  {metrics.VIEWS}
                </div>
                {job.featured && (
                  <div
                    className="public-button-secondary px-4"
                    aria-label={`${job.applicants} applicant${job.applicants === 1 ? '' : 's'}`}
                  >
                    <TeamOutlined />
                    {job.applicants}{' '}
                    {job.applicants === 1 ? 'applicant' : 'applicants'}
                  </div>
                )}
                <button
                  className="public-button-secondary px-4"
                  onClick={shareJob}
                >
                  <ShareAltOutlined />
                  Share
                </button>
              </div>
            </div>
          </aside>
        </section>

        {aboutUs && job.featured && (
          <section className="public-panel">
            <div className="public-panel-body">
              <h2 className="jo-heading mb-4 text-xl font-black">
                About {job.company}
              </h2>
              {containsHtml(aboutUs) ? (
                <div
                  className="job-description-rich"
                  dangerouslySetInnerHTML={{
                    __html: normalizeFeaturedRichHtml(aboutUs),
                  }}
                />
              ) : (
                <div className="job-description-plain">{aboutUs}</div>
              )}
            </div>
          </section>
        )}
      </div>

      <Application isOpen={isOpen} job={job} onClose={() => setIsOpen(false)} />
    </main>
  );
};

export default Detail;
